// Support for "show lead" CSVs — the Loops-flavored format Elijah uses to log
// booth scans after trade shows (NAB 2026, SIGGRAPH 2026). Headers are Loops
// contact-property names (firstName, userGroup, metAt, ...) rather than the
// CRM importer's schema (first_name, ...). When a file in this format is
// dropped on the Contacts import button we map each row into the classic
// import schema and keep the original Loops-shaped payload alongside it so the
// same import can (optionally) create the contact in Loops with full fidelity.
import type { ContactImportSchema } from "./useContactImport";

// Raw row as parsed from a show-lead CSV. Only the headers we map are listed;
// mailing-list columns are dynamic (`loopsMailingList-<id>-<name>`) and read
// by prefix.
export type ShowLeadRow = Record<string, string | undefined>;

// What the loops_import edge function needs per contact. `properties` are
// passed through to Loops verbatim as custom/standard contact properties.
export type LoopsImportContact = {
  email: string;
  firstName?: string;
  lastName?: string;
  // Consent recorded at the booth. The edge function only applies this when
  // the contact does NOT already exist in Loops — existing subscription state
  // is never overwritten (see supabase/functions/_shared/loops.ts history).
  subscribed: boolean;
  mailingLists: Record<string, boolean>;
  properties: Record<string, string>;
};

export type MappedShowLead = {
  crm: ContactImportSchema;
  loops: LoopsImportContact;
};

const MAILING_LIST_PREFIX = "loopsMailingList-";

// Properties forwarded to Loops verbatim when present. `company` is what the
// show-lead format fills; `companyName` is what the CRM→Loops sync writes —
// we send the value under both keys so segmentation works against either.
const LOOPS_PASSTHROUGH_PROPERTIES = [
  "source",
  "userGroup",
  "notes",
  "company",
  "title",
  "phone",
  "city",
  "country",
  "metAt",
  "tags",
] as const;

export function isShowLeadRow(row: object): boolean {
  return "firstName" in row && "userGroup" in row && !("first_name" in row);
}

const clean = (value: string | undefined): string => (value ?? "").trim();

// The pipe-separated Loops tags plus the userGroup become CRM tags, so tier
// filtering (tier-1 ... tier-4-nurture) works in the CRM list views.
export function showLeadTags(row: ShowLeadRow): string[] {
  const tags = clean(row.tags)
    .split("|")
    .map((tag) => tag.trim())
    .filter(Boolean);
  const userGroup = clean(row.userGroup);
  if (userGroup && !tags.includes(userGroup)) tags.push(userGroup);
  return tags;
}

// Fields with no dedicated CRM column are folded into the background note,
// one per line, so nothing from the scan is lost.
export function showLeadBackground(row: ShowLeadRow): string {
  const lines: string[] = [];
  if (clean(row.notes)) lines.push(clean(row.notes));
  if (clean(row.metAt)) lines.push(`Met at: ${clean(row.metAt)}`);
  const place = [clean(row.city), clean(row.country)]
    .filter(Boolean)
    .join(", ");
  if (place) lines.push(`Location: ${place}`);
  if (clean(row.source)) lines.push(`Source: ${clean(row.source)}`);
  return lines.join("\n");
}

export function mapShowLeadRow(row: ShowLeadRow): MappedShowLead {
  // Only opt-ins are forwarded. A show-lead "false" means "did not opt into
  // this list at the booth", never "remove them from it" — and sending
  // list ids that don't exist on the target team (e.g. a dev/sandbox team)
  // would fail the upsert for no benefit.
  const mailingLists: Record<string, boolean> = {};
  for (const [header, value] of Object.entries(row)) {
    if (!header.startsWith(MAILING_LIST_PREFIX)) continue;
    // Header shape: loopsMailingList-<listId>-<human name>
    const listId = header.slice(MAILING_LIST_PREFIX.length).split("-")[0];
    if (listId && clean(value) === "true") mailingLists[listId] = true;
  }

  const properties: Record<string, string> = {};
  for (const key of LOOPS_PASSTHROUGH_PROPERTIES) {
    const value = clean(row[key]);
    if (value) properties[key] = value;
  }
  if (properties.company) properties.companyName = properties.company;

  return {
    crm: {
      first_name: clean(row.firstName),
      last_name: clean(row.lastName),
      gender: "",
      title: clean(row.title),
      company: clean(row.company),
      email: clean(row.email),
      phone: clean(row.phone),
      background: showLeadBackground(row),
      avatar: "",
      first_seen: "",
      last_seen: "",
      status: "",
      tags: showLeadTags(row).join(","),
      linkedin_url: "",
      record_type: "lead",
    },
    loops: {
      email: clean(row.email).toLowerCase(),
      firstName: clean(row.firstName) || undefined,
      lastName: clean(row.lastName) || undefined,
      subscribed: clean(row.subscribed) === "true",
      mailingLists,
      properties,
    },
  };
}

// --- Company matching -------------------------------------------------------
//
// The CRM has >1,300 companies imported from several sources; show CSVs spell
// some of them differently ("USC" vs "Usc", "Golden Era Productions" vs
// "Goldeneraproductions"). Exact-name matching would create duplicates, and
// silent fuzzy merging risks fusing different companies. Policy:
//   - match exact, then case-insensitive, then normalized (rule-provable)
//   - create everything else, but WARN when a close-but-unproven existing
//     name exists, so the periodic cleanup pass can review the pair.

// Normalize a company name for rule-based matching: lowercase, strip
// punctuation and whitespace, drop common legal suffixes.
export function normalizeCompanyName(name: string): string {
  return name
    .toLowerCase()
    .replace(/\b(inc|llc|ltd|corp|co|gmbh|s\.?a\.?)\b\.?/g, "")
    .replace(/[^a-z0-9]/g, "");
}

// Sørensen–Dice bigram similarity, 0..1. Cheap and dependency-free; only used
// to surface warnings, never to merge.
export function companySimilarity(a: string, b: string): number {
  const bigrams = (s: string): Map<string, number> => {
    const map = new Map<string, number>();
    const chars = s.toLowerCase().replace(/\s+/g, " ");
    for (let i = 0; i < chars.length - 1; i++) {
      const bigram = chars.slice(i, i + 2);
      map.set(bigram, (map.get(bigram) ?? 0) + 1);
    }
    return map;
  };
  const mapA = bigrams(a);
  const mapB = bigrams(b);
  if (mapA.size === 0 || mapB.size === 0) return a === b ? 1 : 0;
  let intersection = 0;
  for (const [bigram, count] of mapA) {
    intersection += Math.min(count, mapB.get(bigram) ?? 0);
  }
  let sizeA = 0;
  for (const count of mapA.values()) sizeA += count;
  let sizeB = 0;
  for (const count of mapB.values()) sizeB += count;
  return (2 * intersection) / (sizeA + sizeB);
}

export const COMPANY_SIMILARITY_WARN_THRESHOLD = 0.82;
